/**
 * Keyboard decisions of a modal dialog, kept pure so they can be unit-tested in the node env (the
 * app has no component-test setup). `Modal.tsx` wires them to real DOM events.
 */

export interface KeyLike {
  key: string;
  shiftKey?: boolean;
}

/** Escape dismisses a dialog — and nothing else does (BUG-14: no key closed anything). */
export const isDismissKey = (event: KeyLike): boolean => event.key === 'Escape';

/**
 * Where Tab should land so the focus stays inside the dialog: the element that would leave the
 * dialog wraps to the other end. Returns `null` when the browser's own move is already inside.
 *
 * `focusables` is the dialog's tabbable elements in DOM order; `active` is the element that has the
 * focus right now (the dialog container itself when nothing inside was focused yet).
 */
export const focusAfterTab = <T>(
  event: KeyLike,
  focusables: readonly T[],
  active: T | null
): T | null => {
  if (event.key !== 'Tab' || focusables.length === 0) {
    return null;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const index = active === null ? -1 : focusables.indexOf(active);

  if (event.shiftKey) {
    // Backwards from the first element (or from the container) leaves the dialog: wrap to the last.
    return index <= 0 ? last : null;
  }

  // Forwards from the last element (or from outside the list) leaves the dialog: wrap to the first.
  return index === -1 || index === focusables.length - 1 ? first : null;
};
